import React, { useState, useMemo } from "react";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "../ui/table";
import { 
  Calendar as CalendarIcon, 
  Users, 
  BookOpen, 
  Download, 
  Search, 
  Filter, 
  ArrowUpRight, 
  ArrowDownRight, 
  ChevronRight,
  History,
  TrendingUp,
  FileBarChart,
  UserCheck,
  LayoutDashboard
} from "lucide-react";
import { Input } from "../ui/input";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar } from "../ui/calendar";
import { cn } from "../ui/utils";
import { Progress } from "../ui/progress";
import { toast } from "sonner";

type AttendanceSession = {
  id: string;
  courseName: string;
  courseCode: string;
  section: string;
  date: string;
  teacher: string;
  totalStudents: number;
  presentCount: number;
  absentCount: number;
  lateCount: number;
  rate: number;
  status: "synced" | "pending";
};

const historicalSessions: AttendanceSession[] = [
  { id: "S1", courseName: "Data Structures", courseCode: "CS101", section: "SEC-A", date: "2026-04-18", teacher: "Dr. Robert Fox", totalStudents: 45, presentCount: 42, absentCount: 3, lateCount: 0, rate: 93, status: "synced" },
  { id: "S2", courseName: "Database Systems", courseCode: "CS201", section: "SEC-B", date: "2026-04-17", teacher: "Jane Cooper", totalStudents: 38, presentCount: 30, absentCount: 5, lateCount: 3, rate: 79, status: "synced" },
  { id: "S3", courseName: "Web Development", courseCode: "CS301", section: "SEC-A", date: "2026-04-16", teacher: "Guy Hawkins", totalStudents: 40, presentCount: 39, absentCount: 1, lateCount: 0, rate: 97, status: "synced" },
  { id: "S4", courseName: "Data Structures", courseCode: "CS101", section: "SEC-A", date: "2026-04-15", teacher: "Dr. Robert Fox", totalStudents: 45, presentCount: 38, absentCount: 7, lateCount: 0, rate: 84, status: "synced" },
  { id: "S5", courseName: "Operating Systems", courseCode: "CS202", section: "SEC-C", date: "2026-04-14", teacher: "Esther Howard", totalStudents: 42, presentCount: 40, absentCount: 2, lateCount: 0, rate: 95, status: "synced" },
  { id: "S6", courseName: "Mobile App Dev", courseCode: "CS401", section: "SEC-A", date: "2026-04-13", teacher: "Cameron Williamson", totalStudents: 35, presentCount: 35, absentCount: 0, lateCount: 0, rate: 100, status: "synced" },
];

export function ManageAttendance() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCourse, setSelectedCourse] = useState("all");
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date } | undefined>(undefined);
  const [selectedSession, setSelectedSession] = useState<AttendanceSession | null>(null);

  const filteredSessions = useMemo(() => {
    return historicalSessions.filter(session => {
      const matchesSearch = session.courseName.toLowerCase().includes(searchTerm.toLowerCase()) || 
                            session.courseCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            session.teacher.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCourse = selectedCourse === "all" || session.courseCode === selectedCourse;
      return matchesSearch && matchesCourse;
    });
  }, [searchTerm, selectedCourse]);

  const stats = useMemo(() => {
    const avgRate = Math.round(historicalSessions.reduce((acc, s) => acc + s.rate, 0) / historicalSessions.length);
    const totalPresent = historicalSessions.reduce((acc, s) => acc + s.presentCount, 0);
    const totalStudents = historicalSessions.reduce((acc, s) => acc + s.totalStudents, 0);
    const overallRate = Math.round((totalPresent / totalStudents) * 100);
    
    return { avgRate, overallRate, totalSessions: historicalSessions.length, highPerforming: "CS401" };
  }, []);

  const handleExport = () => {
    toast.success("Preparing historical report...", {
      description: "Check your downloads folder in a few seconds."
    });
  };

  return (
    <div className="p-4 md:p-6 space-y-8 bg-background/50 min-h-screen">
      {/* --- HEADER --- */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div className="space-y-1">
          <h1 className="text-3xl font-black tracking-tight text-foreground/90 flex items-center gap-3">
             <div className="p-2 bg-primary/10 rounded-xl">
                <History className="h-7 w-7 text-primary" />
             </div>
             Attendance History
          </h1>
          <p className="text-muted-foreground font-medium text-sm ml-12">
            Review past attendance records, trends, and <span className="text-primary font-bold">performance reports</span>
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button variant="outline" className="glass-effect-premium border-white/10 h-11 px-4" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export History
          </Button>
          <Button className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/20 h-11 px-6 font-bold">
            <FileBarChart className="mr-2 h-4 w-4" />
            Detailed Reports
          </Button>
        </div>
      </div>

      {/* --- QUICK STATS --- */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Overall Attendance", value: `${stats.overallRate}%`, trend: "+2.4%", icon: TrendingUp, color: "text-emerald-500", bg: "bg-emerald-500/10", up: true },
          { label: "Avg. Session Rate", value: `${stats.avgRate}%`, trend: "-1.2%", icon: BookOpen, color: "text-blue-500", bg: "bg-blue-500/10", up: false },
          { label: "Total Sessions", value: stats.totalSessions, trend: "+5", icon: LayoutDashboard, color: "text-amber-500", bg: "bg-amber-500/10", up: true },
          { label: "Top Course", value: stats.highPerforming, trend: "Consistent", icon: UserCheck, color: "text-primary", bg: "bg-primary/10", up: true },
        ].map((item, i) => (
          <Card key={i} className="glass-card border-white/5 relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
               <item.icon className="h-16 w-16" />
            </div>
            <CardContent className="p-4 flex items-center gap-4 relative z-10">
              <div className={cn("p-3 rounded-xl", item.bg)}>
                <item.icon className={cn("h-5 w-5", item.color)} />
              </div>
              <div>
                <p className="text-[10px] uppercase font-black tracking-widest text-muted-foreground">{item.label}</p>
                <div className="flex items-baseline gap-2">
                   <p className="text-2xl font-black tracking-tighter">{item.value}</p>
                   <span className={cn("text-[10px] font-bold px-1.5 py-0.5 rounded-full", item.up ? "bg-emerald-500/10 text-emerald-500" : "bg-rose-500/10 text-rose-500")}>
                      {item.up ? <ArrowUpRight className="inline h-2 w-2 mr-0.5" /> : <ArrowDownRight className="inline h-2 w-2 mr-0.5" />}
                      {item.trend}
                   </span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* --- DASHBOARD CONTENT --- */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        
        {/* LEFT: SESSION LIST */}
        <div className="xl:col-span-2 space-y-6">
          {/* FILTER BAR */}
          <Card className="glass-card border-white/10 shadow-xl overflow-visible">
            <CardContent className="p-4 md:p-6 grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
               <div className="space-y-2">
                  <label className="text-[11px] font-black uppercase tracking-widest text-muted-foreground ml-1">Search Sessions</label>
                  <div className="relative group">
                     <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                     <Input 
                        placeholder="Course, Code, Teacher..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 glass-effect h-10 border-white/10 focus-visible:ring-primary/50"
                     />
                  </div>
               </div>

               <div className="space-y-2">
                  <label className="text-[11px] font-black uppercase tracking-widest text-muted-foreground ml-1">By Course</label>
                  <Select value={selectedCourse} onValueChange={setSelectedCourse}>
                    <SelectTrigger className="glass-effect h-10 border-white/10">
                      <SelectValue placeholder="All Courses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Courses</SelectItem>
                      <SelectItem value="CS101">CS101 - Data Structures</SelectItem>
                      <SelectItem value="CS201">CS201 - Databases</SelectItem>
                      <SelectItem value="CS301">CS301 - Web Dev</SelectItem>
                    </SelectContent>
                  </Select>
               </div>

               <div className="space-y-2">
                  <label className="text-[11px] font-black uppercase tracking-widest text-muted-foreground ml-1">Status Filter</label>
                  <div className="flex gap-2">
                     <Button variant="outline" className="flex-1 glass-effect border-white/10 h-10 gap-2 font-bold text-xs">
                        <Filter className="h-3.5 w-3.5 opacity-50" />
                        More Filters
                     </Button>
                     <Button variant="outline" size="icon" className="h-10 w-10 glass-effect border-white/10">
                        <CalendarIcon className="h-4 w-4 opacity-50" />
                     </Button>
                  </div>
               </div>
            </CardContent>
          </Card>

          {/* SESSIONS TABLE */}
          <Card className="glass-card border-white/10 shadow-2xl overflow-hidden">
            <Table>
              <TableHeader className="bg-muted/50 border-white/5">
                <TableRow className="hover:bg-transparent">
                  <TableHead className="font-bold uppercase tracking-widest text-[11px] py-4">Date & Session</TableHead>
                  <TableHead className="font-bold uppercase tracking-widest text-[11px]">Course Details</TableHead>
                  <TableHead className="text-center font-bold uppercase tracking-widest text-[11px]">Attendance %</TableHead>
                  <TableHead className="font-bold uppercase tracking-widest text-[11px]">Inspected By</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSessions.map((session) => (
                  <TableRow 
                    key={session.id} 
                    className="group hover:bg-white/[0.03] transition-colors border-white/5 cursor-pointer"
                    onClick={() => setSelectedSession(session)}
                  >
                    <TableCell className="py-5">
                       <div className="flex flex-col">
                          <span className="font-black text-sm tracking-tight">{new Date(session.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          <span className="text-[10px] font-bold text-muted-foreground uppercase">{session.id} • SYNCED</span>
                       </div>
                    </TableCell>
                    <TableCell>
                       <div className="flex flex-col">
                          <span className="font-bold text-[13px] tracking-tight group-hover:text-primary transition-colors">{session.courseName}</span>
                          <span className="text-[11px] font-medium text-muted-foreground uppercase">{session.courseCode} • {session.section}</span>
                       </div>
                    </TableCell>
                    <TableCell>
                       <div className="flex flex-col items-center gap-1.5 px-4 min-w-[120px]">
                          <div className="flex justify-between w-full text-[10px] font-black uppercase">
                             <span className="text-muted-foreground">{session.presentCount}/{session.totalStudents}</span>
                             <span className={cn(session.rate < 85 ? "text-rose-500" : "text-emerald-500")}>{session.rate}%</span>
                          </div>
                          <Progress 
                            value={session.rate} 
                            className={cn("h-1.5", session.rate < 85 ? "[&>div]:bg-rose-500" : "[&>div]:bg-emerald-500")} 
                          />
                       </div>
                    </TableCell>
                    <TableCell>
                       <span className="text-[12px] font-semibold text-foreground/80">{session.teacher}</span>
                    </TableCell>
                    <TableCell>
                       <Button variant="ghost" size="icon" className="group-hover:bg-primary group-hover:text-white transition-all rounded-full h-8 w-8">
                          <ChevronRight className="h-4 w-4" />
                       </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>

        {/* RIGHT: DETAILS / TRENDS */}
        <div className="space-y-6">
           <Card className="glass-card border-white/10 shadow-xl overflow-hidden min-h-[500px] flex flex-col">
              <CardHeader className="bg-white/5 border-b border-white/5">
                 <CardTitle className="text-sm font-black uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                    <FileBarChart className="h-4 w-4 text-primary" />
                    Session Selection
                 </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col items-center justify-center p-8 text-center space-y-4">
                 {selectedSession ? (
                    <div className="w-full space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                       <div className="space-y-2">
                          <div className="h-20 w-20 bg-primary/10 rounded-3xl flex items-center justify-center mx-auto mb-4">
                             <LayoutDashboard className="h-10 w-10 text-primary" />
                          </div>
                          <h3 className="text-2xl font-black tracking-tight">{selectedSession.courseCode}</h3>
                          <p className="text-sm font-bold text-muted-foreground uppercase tracking-widest">{selectedSession.date}</p>
                       </div>

                       <div className="grid grid-cols-2 gap-4">
                          <div className="p-4 rounded-2xl bg-white/5 border border-white/5 space-y-1">
                             <p className="text-[10px] font-black text-muted-foreground uppercase">Present</p>
                             <p className="text-2xl font-black text-emerald-500">{selectedSession.presentCount}</p>
                          </div>
                          <div className="p-4 rounded-2xl bg-white/5 border border-white/5 space-y-1">
                             <p className="text-[10px] font-black text-muted-foreground uppercase">Absent</p>
                             <p className="text-2xl font-black text-rose-500">{selectedSession.absentCount}</p>
                          </div>
                       </div>

                       <div className="space-y-4 pt-4 border-t border-white/5">
                          <Button className="w-full bg-primary hover:bg-primary/90 h-11 font-bold shadow-lg shadow-primary/20">
                             View Full Student List
                          </Button>
                          <Button variant="outline" className="w-full h-11 border-white/10 glass-effect font-bold">
                             Edit Record
                          </Button>
                       </div>
                    </div>
                 ) : (
                    <>
                       <div className="h-16 w-16 bg-white/5 rounded-2xl flex items-center justify-center mb-4">
                          <History className="h-8 w-8 text-muted-foreground/30" />
                       </div>
                       <div>
                          <p className="font-black text-lg tracking-tight">Select a Session</p>
                          <p className="text-[13px] font-medium text-muted-foreground max-w-[200px] mx-auto mt-1">
                             Click on any record from the list to see detailed student breakdown and statistics.
                          </p>
                       </div>
                    </>
                 )}
              </CardContent>
           </Card>

           <Card className="glass-card border-emerald-500/20 shadow-emerald-500/5 overflow-hidden">
              <CardContent className="p-6 space-y-4">
                 <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-500/10 rounded-lg">
                       <TrendingUp className="h-4 w-4 text-emerald-500" />
                    </div>
                    <span className="text-[11px] font-black uppercase tracking-widest">AI Insights</span>
                 </div>
                 <p className="text-[13px] font-medium text-foreground/80 leading-relaxed">
                    Attendance for <span className="text-emerald-500 font-bold">Data Structures</span> has improved by **12%** since last month. Keep it up!
                 </p>
              </CardContent>
           </Card>
        </div>
      </div>
    </div>
  );
}
